import { DIFFICULTIES, type Difficulty, type OneTimeActivity } from '@life-rpg/shared';
import React, { useState } from 'react';

import type { OneTimeActivityRepository } from './activityRepository.js';
import { titleCase, useActivityList } from './activityUi.js';
import { CompletedOneTimeActivities } from './CompletedOneTimeActivities.js';
import { OneTimeActivityConversion } from './OneTimeActivityConversion.js';

interface OneTimeActivitiesProps {
  repository: OneTimeActivityRepository;
  today?: () => Date;
}

interface OneTimeEditForm {
  id: string;
  name: string;
  difficulty: Difficulty;
}

const listActiveOneTimeActivities = (repository: OneTimeActivityRepository) =>
  repository.listActive();
const listCompletedOneTimeActivities = (repository: OneTimeActivityRepository) =>
  repository.listCompleted();

export const OneTimeActivities = ({
  repository,
  today = () => new Date(),
}: OneTimeActivitiesProps): React.JSX.Element => {
  const {
    activities,
    setActivities,
    problem: activeLoadProblem,
  } = useActivityList<OneTimeActivity, OneTimeActivityRepository>(
    repository,
    listActiveOneTimeActivities,
  );
  const {
    activities: completedActivities,
    setActivities: setCompletedActivities,
    problem: completedLoadProblem,
  } = useActivityList<OneTimeActivity, OneTimeActivityRepository>(
    repository,
    listCompletedOneTimeActivities,
    'Could not load completed activities.',
  );
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [completingId, setCompletingId] = useState<string | null>(null);
  const [archivingId, setArchivingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<OneTimeEditForm | null>(null);
  const [conversionActivity, setConversionActivity] = useState<OneTimeActivity | null>(null);
  const [actionProblem, setProblem] = useState<string | null>(null);
  const problem = actionProblem ?? activeLoadProblem ?? completedLoadProblem;

  const createActivity = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setProblem(null);
    const cleanName = name.trim();
    if (cleanName === '') {
      setProblem('Name cannot be blank.');
      return;
    }

    setSaving(true);
    try {
      const created = await repository.create({ name: cleanName, difficulty: 'medium' });
      setActivities((current) => [...(current ?? []), created]);
      setName('');
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'Could not save activity.');
    } finally {
      setSaving(false);
    }
  };

  const beginEdit = (activity: OneTimeActivity): void => {
    setProblem(null);
    setEditForm({ id: activity.id, name: activity.name, difficulty: activity.difficulty });
  };

  const cancelEdit = (): void => {
    setEditForm(null);
  };

  const updateActivity = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (editForm === null) return;
    setProblem(null);
    const cleanName = editForm.name.trim();
    if (cleanName === '') {
      setProblem('Name cannot be blank.');
      return;
    }

    setSaving(true);
    try {
      const updated = await repository.update(editForm.id, {
        name: cleanName,
        difficulty: editForm.difficulty,
      });
      setActivities(
        (current) =>
          current?.map((activity) => (activity.id === editForm.id ? updated : activity)) ?? [
            updated,
          ],
      );
      cancelEdit();
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'Could not save activity.');
    } finally {
      setSaving(false);
    }
  };

  const completeActivity = async (id: string): Promise<void> => {
    setProblem(null);
    setCompletingId(id);
    try {
      const completed = await repository.complete(id);
      setActivities((current) => current?.filter((activity) => activity.id !== id) ?? []);
      setCompletedActivities((current) => [completed, ...(current ?? [])]);
      if (editForm?.id === id) cancelEdit();
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'Could not complete activity.');
    } finally {
      setCompletingId(null);
    }
  };

  const archiveActivity = async (id: string): Promise<void> => {
    setProblem(null);
    setArchivingId(id);
    try {
      await repository.archive(id);
      setActivities((current) => current?.filter((activity) => activity.id !== id) ?? []);
      if (editForm?.id === id) cancelEdit();
      if (conversionActivity?.id === id) setConversionActivity(null);
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'Could not archive activity.');
    } finally {
      setArchivingId(null);
    }
  };

  const beginConversion = (activity: OneTimeActivity): void => {
    setProblem(null);
    setConversionActivity(activity);
  };

  const finishConversion = (convertedId: string): void => {
    setActivities((current) => current?.filter((activity) => activity.id !== convertedId) ?? []);
    setCompletedActivities(
      (current) => current?.filter((activity) => activity.id !== convertedId) ?? [],
    );
    setConversionActivity(null);
    if (editForm?.id === convertedId) cancelEdit();
  };

  return (
    <section aria-labelledby="one-time-activities-heading">
      <h2 id="one-time-activities-heading">One-time activities</h2>

      <form className="one-time-form" onSubmit={(event) => void createActivity(event)}>
        <label>
          One-time activity
          <input required value={name} onChange={(event) => setName(event.target.value)} />
        </label>
        <button disabled={saving} type="submit">
          {saving ? 'Adding…' : 'Add'}
        </button>
      </form>

      {editForm !== null && (
        <form className="activity-form" onSubmit={(event) => void updateActivity(event)}>
          <label>
            Name
            <input
              required
              value={editForm.name}
              onChange={(event) =>
                setEditForm((current) =>
                  current === null ? null : { ...current, name: event.target.value },
                )
              }
            />
          </label>
          <label>
            Difficulty
            <select
              value={editForm.difficulty}
              onChange={(event) =>
                setEditForm((current) =>
                  current === null
                    ? null
                    : { ...current, difficulty: event.target.value as Difficulty },
                )
              }
            >
              {DIFFICULTIES.map((difficulty) => (
                <option key={difficulty} value={difficulty}>
                  {titleCase(difficulty)}
                </option>
              ))}
            </select>
          </label>
          <div className="form-actions">
            <button disabled={saving} type="submit">
              {saving ? 'Saving…' : 'Save changes'}
            </button>
            <button className="secondary" disabled={saving} type="button" onClick={cancelEdit}>
              Cancel editing
            </button>
          </div>
        </form>
      )}

      {conversionActivity !== null && (
        <OneTimeActivityConversion
          key={conversionActivity.id}
          activity={conversionActivity}
          anchor={
            conversionActivity.completedAt === undefined
              ? today()
              : new Date(conversionActivity.completedAt)
          }
          repository={repository}
          onCancel={() => setConversionActivity(null)}
          onConverted={finishConversion}
          onProblem={setProblem}
        />
      )}

      {problem !== null && <p role="alert">{problem}</p>}
      {activities === null && problem === null && <p>Loading activities…</p>}
      {activities?.length === 0 && <p>No one-time activities yet.</p>}
      {activities !== null && activities.length > 0 && (
        <ul className="activity-list">
          {activities.map((activity) => (
            <li key={activity.id}>
              <article className="activity-card">
                <h3>{activity.name}</h3>
                <p>{titleCase(activity.difficulty)}</p>
                <div className="card-actions">
                  <button className="secondary" type="button" onClick={() => beginEdit(activity)}>
                    Edit
                  </button>
                  <button
                    disabled={completingId === activity.id}
                    type="button"
                    onClick={() => void completeActivity(activity.id)}
                  >
                    {completingId === activity.id ? 'Completing…' : 'Complete'}
                  </button>
                  <button
                    className="secondary"
                    type="button"
                    onClick={() => beginConversion(activity)}
                  >
                    Make recurring
                  </button>
                  <button
                    className="danger"
                    disabled={archivingId === activity.id}
                    type="button"
                    onClick={() => void archiveActivity(activity.id)}
                  >
                    {archivingId === activity.id ? 'Archiving…' : 'Archive'}
                  </button>
                </div>
              </article>
            </li>
          ))}
        </ul>
      )}

      <CompletedOneTimeActivities
        activities={completedActivities}
        loadFailed={completedLoadProblem !== null}
        onConvert={beginConversion}
      />
    </section>
  );
};
